<template>
  <div>
    <ui-stage>
      <ui-alert type="success" :closable="false">
        <ui-flex row style="padding-bottom: 15px">
          <div>
            <h3>日期</h3>
            <ui-datetime-picker v-model="date" type="date" />
          </div>
          <div class="ml">
            <h3>项目</h3>
            <ui-admin-model-picker v-model="projectId" :config="projectPickerConfig" />
          </div>
          <div class="ml" v-if="canSaveConfig">
            <h3>管理字段</h3>
            <router-link :to="configUrl">
              <ui-button icon="el-icon-setting">编辑字段</ui-button>
            </router-link>
          </div>
        </ui-flex>
      </ui-alert>
      <div v-if="sections && sections.length > 0" class="mb">
        <el-tabs v-model="section" @tab-click="handleTabClick">
          <el-tab-pane
            v-for="sectionItem in sections"
            :key="sectionItem"
            :label="sectionItem"
            :name="sectionItem"
          />
          <el-tab-pane name="预览">
            <span slot="label"> <i class="el-icon-view"></i> 预览 </span>
          </el-tab-pane>
        </el-tabs>
      </div>
      <UiAlert type="warning">请注意拍摄照片的方向，应当垂直向上，请勿上传横向或斜向的照片</UiAlert>
      <ui-admin-form
        v-if="entryFields && saveData"
        class="mt"
        :readonly="!effectiveCanEdit"
        :fields="entryFields"
        :cols="cols"
        :model="data"
        @submit="handleSubmit"
      />
      <SupervisionSignOffNav :projectId="projectId" />
    </ui-stage>
  </div>
</template>

<script>
import { _, moment } from '@yishitec/web';
import UiAdminModelPicker from '@yishitec/web/components/UiAdminModelPicker.vue';
import permissions from '../../../mixins/permissions';
import SupervisionSignOffNav from '../../../components/SupervisionSignOffNav.vue';

const projectPickerConfig = {
  title: '项目',
  name: 'projectId',
  model: {
    name: 'Project',
    titleFields: ['title'],
    type: 'string',
    appData: {
      apiName: 'projects',
    },
  },
  type: 'model',
};

export default {
  name: 'List',
  components: {
    UiAdminModelPicker,
    SupervisionSignOffNav,
  },
  mixins: [permissions],
  data() {
    return {
      date: moment().format('YYYY-MM-DD'),
      projectId: '',
      data: null,
      entryFields: [],
      projectPickerConfig,
      saveData: {},
      cols: 1,
      companyName: '',
      sections: [],
      section: '',
      originalData: null,
      effectiveCurrentSection: '',
    };
  },
  computed: {
    canSave() {
      return this.amI(this.projectId, '监理单位', '管理人员', true);
    },
    canSaveConfig() {
      return this.amI(null, null, '平台管理员');
    },
    configUrl() {
      return `/admin/supervision-logs/config/${encodeURIComponent(this.companyName)}`;
    },
    isFormDirty() {
      if (!this.originalData || !this.data || this.isPreviewMode) {
        return false;
      }
      return JSON.stringify(this.data) !== JSON.stringify(this.originalData);
    },
    isPreviewMode() {
      return this.effectiveCurrentSection === '预览';
    },
    currentSectionName() {
      if (this.isPreviewMode) {
        return null;
      }
      return this.effectiveCurrentSection;
    },
    effectiveCanEdit() {
      return this.canSave && !this.isPreviewMode;
    },
  },
  watch: {
    currentProjects() {
      this.projectId = _.get(this, 'currentProjects.0');
    },
    projectId() {
      this.loadData();
    },
    date() {
      this.loadData();
    },
  },
  mounted() {
    this.projectId = _.get(this, 'currentProjects.0');
  },
  methods: {
    async loadData() {
      if (!this.projectId) {
        this.projectId = _.get(this, 'currentProjects.0');
      }
      if (!this.projectId) {
        this.$message({
          type: 'error',
          message: '请选择一个项目',
        });
        return;
      }
      let apiUrl;
      if (this.isPreviewMode) {
        apiUrl = `/api/supervision-logs/edit/${encodeURIComponent(
          this.projectId,
        )}/${encodeURIComponent(moment(this.date).format('YYYY-MM-DD'))}`;
      } else if (!this.sections || this.sections.length === 0 || !this.currentSectionName) {
        apiUrl = `/api/supervision-logs/edit/${encodeURIComponent(
          this.projectId,
        )}/${encodeURIComponent(moment(this.date).format('YYYY-MM-DD'))}`;
      } else {
        apiUrl = `/api/supervision-logs/edit/${encodeURIComponent(
          this.projectId,
        )}/${encodeURIComponent(moment(this.date).format('YYYY-MM-DD'))}/${encodeURIComponent(
          this.currentSectionName,
        )}`;
      }
      const res = await this.withLoading(this.api(apiUrl));
      this.saveData = _.get(res, 'item', {});
      let data = _.get(res, 'item.logs', '{}');
      const attributes = _.get(res, 'attributes', []);
      const cols = _.get(res, 'cols', 1);
      const companyName = _.get(res, 'companyName', '');
      const sections = _.get(res, 'sections', []);
      const currentSection = _.get(res, 'currentSection', '');

      if (sections && sections.length > 0) {
        this.sections = sections;
        if (!this.isPreviewMode) {
          if (currentSection) {
            this.effectiveCurrentSection = currentSection;
            this.section = currentSection;
          } else if (!this.effectiveCurrentSection && sections.length > 0) {
            const [firstSection] = sections;
            this.effectiveCurrentSection = firstSection;
            this.section = firstSection;
          } else if (
            this.effectiveCurrentSection &&
            this.effectiveCurrentSection !== '预览' &&
            !sections.includes(this.effectiveCurrentSection)
          ) {
            const [firstSection] = sections;
            this.effectiveCurrentSection = firstSection;
            this.section = firstSection;
          }
        }
      } else {
        this.sections = [];
        if (!this.isPreviewMode) {
          this.effectiveCurrentSection = '';
          this.section = '';
        }
      }

      if (!data) {
        return;
      }
      try {
        data = JSON.parse(data);
      } catch (e) {
        data = {};
        console.log(e);
      }
      this.data = data;
      this.entryFields = attributes;
      this.cols = cols;
      this.companyName = companyName;
      if (!this.isPreviewMode) {
        this.originalData = JSON.parse(JSON.stringify(data));
      }
    },
    async handleTabClick(tab) {
      const newSection = tab.name;
      const previousSection = this.effectiveCurrentSection;

      if (newSection === previousSection) {
        return;
      }

      if (newSection !== '预览' && previousSection !== '预览' && this.isFormDirty) {
        try {
          await this.$confirm('有未提交的修改，是否继续编辑？', '提示', {
            confirmButtonText: '留在当前页面',
            cancelButtonText: '放弃未保存的修改',
            type: 'warning',
          });
          this.$nextTick(() => {
            this.effectiveCurrentSection = previousSection;
          });
          return;
        } catch (e) {
          // User cancelled, continue to switch tab
        }
      }
      this.effectiveCurrentSection = newSection;
      await this.loadData();
    },
    async handleSubmit() {
      if (this.isPreviewMode) {
        this.$message({
          type: 'warning',
          message: '预览模式下不能保存',
        });
        return;
      }
      const body = {
        ...this.saveData,
        logs: JSON.stringify(this.data),
      };
      if (this.sections && this.sections.length > 0 && this.currentSectionName) {
        body.section = this.currentSectionName;
      }
      const res = await this.withLoading(this.api('/api/supervision-logs/save', body));
      if (res) {
        this.$message({
          type: 'success',
          message: '已保存',
        });
        this.loadData();
      }
    },
  },
};
</script>

<style lang='less' scoped></style>
